import { Store, toImmutable, Immutable, KeyPathOrGetter } from 'nuclear-js'
import { postInContext } from '../logic/post-states'
import { mapById, mapByProp, mapFromAToB } from '../utils'
import { Customer, Post, Subscription, Plan, Keyword, Rating, Writer, Account, BillingAccount, Document, Industry,
          StoreState, StandardMap, MapOfMapsById, WithId, CustomerWriterAccess, PostEditRequest } from '../types'
import filter from 'lodash-es/filter'

const storeName: string = 'customer'

enum Operations {
  setCustomers = 'setCustomers',
  setCustomer = 'setCustomer',
  updateCustomer = 'updateCustomer',
  removeCustomer = 'removeCustomer',
  setPosts = 'setPosts',
  setPostsForWriter = 'setPostsForWriter',
  setPost = 'setPost',
  updatePosts = 'updatePosts',
  updatePost = 'updatePost',
  setSubscription = 'setSubscription',
  setKeywords = 'setKeywords',
  updateKeywords = 'updateKeywords',
  updateKeyword = 'updateKeyword',
  deleteKeyword = 'deleteKeyword',
  createKeyword = 'createKeyword',
  setRatings = 'setRatings',
  updateRating = 'updateRating',
  setAccount = 'setAccount',
  setAccountPostCounts = 'setAccountPostCounts',
  updateAccount = 'updateAccount',
  setBillingAccount = 'setBillingAccount',
  setWriter = 'setWriter',
  setWriters = 'setWriters',
  addDocument = 'addDocument',
  removeDocument = 'removeDocument',
  setPlans = 'setPlans',
  setCustomerWriterAccesses = 'setCustomerWriterAccesses',
  setCustomerWriterAccess = 'setCustomerWriterAccess',
  setCustomerWriterAccessForAccount = 'setCustomerWriterAccessForAccount',
  updateCustomerWriterAccess = 'updateCustomerWriterAccess',
  updateCustomerWriterAccessForAccount = 'updateCustomerWriterAccessForAccount',
  setIndustries = 'setIndustries',
  setPostEditRequests = 'setPostEditRequests'
}

const mappedPost = (post: StandardMap, writers: MapOfMapsById, keywords: MapOfMapsById): StandardMap => {
  const ws = writers || Immutable.Map()
  const ks = keywords || Immutable.Map()
  return post.set('writer', ws.get(post.get('writerId')))
             .set('keyword', ks.get(post.get('keywordId')))
}

const mappedPosts = (posts: MapOfMapsById, writers: MapOfMapsById, keywords: MapOfMapsById): Immutable.List<StandardMap> => {
  const ps = posts || Immutable.Map()
  return ps.map((p) => {
    return mappedPost(p, writers, keywords)
  }).toList()
}

const customerIds = (customers: MapOfMapsById): Array<number> => {
  return customers.keySeq().toArray()
}

const getters = {
  shallowCustomers: [
    [storeName, 'customers'],
    (customers: MapOfMapsById): Immutable.List<StandardMap> => customers && customers.toList()
  ],
  customers: [
    [storeName, 'customers'],
    [storeName, 'writers'],
    (customers: MapOfMapsById, writers: MapOfMapsById): Immutable.List<StandardMap> => {
      if (customers) {
        return customers.map((c) => {
          const posts = c.get('posts')
          const keywords = c.get('keywords', Immutable.Map())
          return c.set('posts', mappedPosts(posts, writers, keywords)).set('keywords', keywords.toList())
        }).toList()
      }
    }
  ],
  customer: (id: number): KeyPathOrGetter => [
    [storeName, 'customers', id],
    [storeName, 'writers'],
    (customer: StandardMap, writers: MapOfMapsById): StandardMap => {
      if (customer) {
        const posts = customer.get('posts')
        const keywords = customer.get('keywords', Immutable.Map())
        return customer.set('posts', mappedPosts(posts, writers, keywords)).set('keywords', keywords.toList())
      }
    }
  ],
  posts: (customerId: number, context: string): KeyPathOrGetter => [
    [storeName, 'customers', customerId, 'posts'],
    [storeName, 'writers'],
    [storeName, 'customers', customerId, 'keywords'],
    (posts: MapOfMapsById, writers: MapOfMapsById, keywords: MapOfMapsById): Immutable.List<StandardMap> => {
      const ps = posts || Immutable.Map()
      const postsInContext = <MapOfMapsById>ps.filter(p => postInContext(p.toJS(), context))
      return mappedPosts(postsInContext, writers, keywords)
    }
  ],
  post: (customerId: number, postId: number): KeyPathOrGetter => [
    [storeName, 'customers', customerId, 'posts', postId],
    [storeName, 'writers'],
    [storeName, 'customers', customerId, 'keywords'],
    (post: StandardMap, writers: MapOfMapsById, keywords: MapOfMapsById): StandardMap => {
      return post ? mappedPost(post, writers, keywords) : null
    }
  ],
  postsByWriter: (customerId: number, writerId: number): KeyPathOrGetter => [
    [storeName, 'customers', customerId, 'posts'],
    (posts: MapOfMapsById) => {
      const ps = posts || Immutable.Map()
      return filter(ps, {writerId})
    }
  ],
  keywords: (customerId: number): KeyPathOrGetter => [
    [storeName, 'customers', customerId, 'keywords'],
    (keywordsMap: MapOfMapsById): Immutable.List<StandardMap> => keywordsMap ? keywordsMap.toList() : Immutable.List()
  ],
  subscription: (customerId: number): KeyPathOrGetter => [storeName, 'customers', customerId, 'subscription'],
  account: [storeName, 'account'],
  aggregateAccount: [
    [storeName, 'account'],
    [storeName, 'customers'],
    [storeName, 'writers'],
    (account: StandardMap, customers: MapOfMapsById, writers: MapOfMapsById): StandardMap => {
      if (account) {
        const cids = customerIds(customers)
        const posts = cids.map((cid) => customers.getIn([cid, 'posts'])).reduce((prevPostMap, currPostMap) => prevPostMap.merge(currPostMap), Immutable.Map() )
        const keywords = cids.map((cid) => customers.getIn([cid, 'keywords'], Immutable.Map())).reduce((prevKeywordMap, currKeywordMap) => prevKeywordMap.merge(currKeywordMap), Immutable.Map() )
        return account.set('posts', mappedPosts(posts, writers, keywords)).set('keywords', keywords.toList())
      }
    }
  ],
  accountPostsCount: (writerId: number): KeyPathOrGetter => [storeName, 'account', 'postCounts', writerId],
  billingAccount: [storeName, 'billingAccount'],
  plans: [storeName, 'plans'],
  writer: (id: number): KeyPathOrGetter => [storeName, 'writers', id],
  writers: [
    [storeName, 'writers'],
    (writers: MapOfMapsById) => writers.toList()
  ],
  writerIdFromSlug: (slug: string): KeyPathOrGetter => [storeName, 'writers', slug],
  customerWriterAccess: (writerId: number, customerId: number): KeyPathOrGetter => [storeName, 'customerWriterAccesses', writerId, customerId],
  customerWriterAccessForAccount: (writerId: number): KeyPathOrGetter => [storeName, 'customerWriterAccesses', writerId, 'account'],
  industries: [storeName, 'industries']
 }

function merger(a, b) {
  if (a && a.mergeWith && !Immutable.List.isList(a) && !Immutable.List.isList(b)) {
    return a.mergeWith(merger, b)
  }
  return b
}

const setters: {[setter in Operations]: (state: StoreState, data: any) => StoreState } = {
  setCustomers: (state: StoreState, customers: Customer[]): StoreState => {
    const oldCustomersMap = state.get('customers', Immutable.Map())
    const newCustomersMap = mapById(customers)
    const mergedCustomersSetArrays = oldCustomersMap.mergeWith(merger, newCustomersMap)
    return state.setIn(['customers'], mergedCustomersSetArrays)
  },
  setCustomer: (state: StoreState, customer: Customer): StoreState => {
    return state.mergeDeepIn(['customers'], mapById([customer]))
  },
  updateCustomer: (state: StoreState, data: { customerId: number, updates: object }): StoreState => {
    return state.mergeIn(['customers', data.customerId], data.updates)
  },
  removeCustomer: (state: StoreState, customerId: number): StoreState => {
    return state.deleteIn(['customers', customerId])
  },
  // special logic to deal with post states
  setPosts: (state: StoreState, data: { customerId: number, context: string, total: number, posts: Post[] }): StoreState => {
    const postsMap = state.getIn(['customers', data.customerId, 'posts'], Immutable.Map())
    const otherPostsMap: StandardMap = postsMap.filter(p => !postInContext(p.toJS(), data.context))
    const combinedMap = otherPostsMap.merge(mapById(data.posts))
    const withNewPosts = state.setIn(['customers', data.customerId, 'posts'], combinedMap)
    return withNewPosts
            .setIn(['customers', data.customerId, 'postsLoaded', data.context], true)
            .setIn(['customers', data.customerId, 'postCounts', data.context], data.total)
  },
  setAccountPostCounts: (state: StoreState, data: {context: string, total: number}): StoreState => {
    return state.setIn(['account', 'postCounts', data.context], data.total)
  },
  setPostsForWriter: (state: StoreState, data: { customerId: number, writerId: number, total: number, posts: Post[] }): StoreState => {
    const removed = state.deleteIn(['customers', data.customerId, 'posts'])
    const writerPostsState = removed.setIn(['customers', data.customerId, 'posts'], mapById(data.posts))
    if (data.total) {
      return writerPostsState.setIn(['customers', data.customerId, 'postCounts', data.writerId], data.total)
    } else {
      return writerPostsState
    }
  },
  setPost: (state: StoreState, data: { customerId: number, post: Post }): StoreState => {
    return state.setIn(['customers', data.customerId, 'posts', data.post.id], Immutable.fromJS(data.post))
  },
  updatePosts: (state: StoreState, data: { customerId: number, postUpdates: WithId[] }): StoreState => {
    return state.mergeDeepIn(['customers', data.customerId, 'posts'], mapById(data.postUpdates))
  },
  updatePost: (state: StoreState, data: { customerId: number, postId: number, updates: Object }): StoreState => {
    return state.mergeDeepIn(['customers', data.customerId, 'posts', data.postId], Immutable.fromJS(data.updates))
  },
  setSubscription: (state: StoreState, data: { customerId: number, subscription: Subscription }): StoreState => {
    return state.setIn(['customers', data.customerId, 'subscription'], Immutable.fromJS(data.subscription))
  },
  setKeywords: (state: StoreState, data: { customerId: number, keywords: Keyword[] }): StoreState => {
    return state.mergeIn(['customers', data.customerId, 'keywords'], mapById(data.keywords))
  },
  updateKeywords: (state: StoreState, data: { customerId: number, keywords: Keyword[] }): StoreState => {
    return state.mergeIn(['customers', data.customerId, 'keywords'], mapById(data.keywords))
  },
  updateKeyword: (state: StoreState, data: { customerId: number, keywordId: number, updates: any }): StoreState => {
    return state.mergeDeepIn(['customers', data.customerId, 'keywords', data.keywordId], data.updates)
  },
  deleteKeyword: (state: StoreState, data: { customerId: number, keywordId: number }): StoreState => {
    return state.deleteIn(['customers', data.customerId, 'keywords', data.keywordId])
  },
  createKeyword: (state: StoreState, data: { keyword: Keyword }): StoreState => {
    return state.mergeDeepIn(['customers', data.keyword.customerId, 'keywords', data.keyword.id], data.keyword)
  },
  setRatings: (state: StoreState, data: { customerId: number, ratings: Rating[] }): StoreState => {
    const byPostId = mapByProp('postId', data.ratings)
    const postsMap = state.getIn(['customers', data.customerId, 'posts'], Immutable.Map())
    const newPostsMap = postsMap.reduce((m, p) => {
      const id = p.get('id')
      return m.set(id, p.set('rating', byPostId.get(id)))
    }, Immutable.Map())
    return state.setIn(['customers', data.customerId, 'posts'], newPostsMap)
  },
  setPostEditRequests: (state: StoreState, data: {customerId: number, postId: number, postEditRequests: PostEditRequest[]}): StoreState => {
    return state.mergeDeepIn(['customers', data.customerId, 'posts', data.postId, 'postEditRequests'], mapById(data.postEditRequests))
  },
  updateRating: (state: StoreState, data: { customerId: number, postId: number, updates: Object }): StoreState => {
    return state.mergeDeepIn(['customers', data.customerId, 'posts', data.postId], Immutable.fromJS({ rating: data.updates }))
  },
  setAccount: (state: StoreState, account: Account ): StoreState => {
    return state.mergeIn(['account'], Immutable.fromJS(account))
  },
  updateAccount: (state: StoreState, updates: Object): StoreState => {
    return state.mergeDeepIn(['account'], Immutable.fromJS(updates))
  },
  setBillingAccount: (state: StoreState, billingAccount: BillingAccount): StoreState => {
    return state.setIn(['billingAccount'], Immutable.fromJS(billingAccount))
  },
  setWriter: (state: StoreState, writer: Writer): StoreState => {
    const slugState = state.mergeDeepIn(['writers'], mapFromAToB('slug', 'id', [writer]))
    return slugState.mergeDeepIn(['writers', writer.id], Immutable.fromJS(writer))
  },
  setWriters: (state: StoreState, writers: Writer[]): StoreState => {
    const slugState = state.mergeDeepIn(['writers'], mapFromAToB('slug', 'id', writers))
    return slugState.mergeDeepIn(['writers'], mapById(writers))
  },
  addDocument: (state: StoreState, data: { customerId: number, keywordId: number, document: Document }): StoreState => {
    return state.updateIn(['customers', data.customerId, 'keywords', data.keywordId, 'documents'],
                          (docs: Immutable.List<StandardMap>) => docs.push(Immutable.fromJS(data.document)))
  },
  removeDocument: (state: StoreState, data: { customerId: number, keywordId: number, id: number }): StoreState => {
    return state.updateIn(['customers', data.customerId, 'keywords', data.keywordId, 'documents'],
                          (docs: Immutable.List<StandardMap>) => docs.filter((d) => d.get('id') !== data.id))
  },
  setPlans: (state: StoreState, plans: Plan[]): StoreState => {
    return state.set('plans', Immutable.fromJS(plans))
  },
  setCustomerWriterAccesses: (state: StoreState, data: {customerWriterAccesses: CustomerWriterAccess[], writerId: number}): StoreState => {
    return state.mergeIn(['customerWriterAccesses', data.writerId], mapByProp('customerId', data.customerWriterAccesses))
  },
  setCustomerWriterAccess: (state: StoreState, customerWriterAccess: CustomerWriterAccess ): StoreState => {
    return state.setIn(['customerWriterAccesses', customerWriterAccess.writerId, customerWriterAccess.customerId], Immutable.fromJS(customerWriterAccess))
  },
  setCustomerWriterAccessForAccount: (state: StoreState, customerWriterAccess: CustomerWriterAccess): StoreState => {
    return state.setIn(['customerWriterAccesses', customerWriterAccess.writerId, 'account'], Immutable.fromJS(customerWriterAccess))
  },
  updateCustomerWriterAccess: (state: StoreState, data: { writerId: number, customerId: number, updates: {access: string} }): StoreState => {
    const cwaState = state.mergeIn(['customerWriterAccesses', data.writerId, data.customerId], Immutable.fromJS(data.updates))
    const accountCwa: CustomerWriterAccess = cwaState.getIn(['customerWriterAccesses', data.writerId, 'account'], Immutable.Map()).toJS()
    const accountAccess: string = accountCwa && accountCwa.access
    const customerAccess: string = data.updates.access

    const postsMap = cwaState.getIn(['customers', data.customerId, 'posts'], Immutable.Map())
    let postsByWriter = postsMap.toList().toJS().filter(p => p.writerId === data.writerId)
    postsByWriter.forEach(p => {
        p['writerBlocked'] = customerAccess === 'blocked' ? 'customer' : (customerAccess !== 'preferred' && accountAccess === 'blocked' ? 'account' : null)
        p['writerPreferred'] = customerAccess === 'preferred' ? 'customer' : (customerAccess !== 'blocked' && accountAccess === 'preferred' ? 'account' : null)
    })
    return cwaState.mergeIn(['customers', data.customerId, 'posts'], mapById(postsByWriter))
  },
  updateCustomerWriterAccessForAccount: (state: StoreState, data: {writerId: number, updates: {access: string}}): StoreState => {
    const cwaState = state.mergeIn(['customerWriterAccesses', data.writerId, 'account'], Immutable.fromJS(data.updates))
    let returnState = cwaState
    const customers = cwaState.getIn(['customers'], Immutable.Map()).toList().toJS()
    const accountAccess: string = data.updates.access
    customers.forEach(c => {
      const customerCwa: CustomerWriterAccess = state.getIn(['customerWriterAccesses', data.writerId, c.id], Immutable.Map()).toJS()
      const customerAccess: string = customerCwa && customerCwa.access

      const postsMap = returnState.getIn(['customers', c.id, 'posts'], Immutable.Map())
      const postsByWriter = postsMap.toList().toJS().filter(p => p.writerId === data.writerId)
      postsByWriter.forEach(p => {
        p['writerBlocked'] = customerAccess === 'blocked' ? 'customer' : (customerAccess !== 'preferred' && accountAccess === 'blocked' ? 'account' : null)
        p['writerPreferred'] = customerAccess === 'preferred' ? 'customer' : (customerAccess !== 'blocked' && accountAccess === 'preferred' ? 'account' : null)
      })
      returnState = returnState.mergeIn(['customers', c.id, 'posts'], mapById(postsByWriter))
    })
    return returnState
  },
  setIndustries: (state: StoreState, industries: Industry[]): StoreState => {
    return state.set('industries', Immutable.fromJS(industries))
  },
}

const store = new Store({
  getInitialState() {
    return toImmutable({
      customers: {},
      account: {},
      writers: {},
      plans: [],
    })
  },
  initialize(this: Store): void {
    Object.keys(Operations).forEach((o: Operations) => {
      this.on(o, setters[o])
    })
  }
})


export {
  store as customerStore,
  Operations as customerOperations,
  getters as customerGetters,
  setters as customerSetters
}
